/**
 * Shared address-autocomplete input for US addresses.
 *
 * Feb 2026 rewrite — user complaint: OSM alone was returning completely
 * unrelated addresses ("1736 Neptune Dr Las Vegas" → matched "2000
 * Fashion Show Drive Paradise").  Fix strategy:
 *
 *   Backend proxy at `GET /api/address/search` merges Photon (fast
 *   prefix, OSM) + US Census Geocoder (authoritative US address
 *   database — no CORS on their side, so we HAVE to proxy).  Photon
 *   results whose house number doesn't match the query are dropped.
 *   Results tagged `source: "census"` are the deliverable USPS-verified
 *   matches and render with a "USPS verified" badge.
 *
 * Falls back gracefully to plain typing when the backend / network is
 * unavailable.
 *
 * Usage (unchanged):
 *   <AddressAutocomplete
 *     label="Home Address *"
 *     value={form.address}
 *     onChange={(e) => setForm(...)}
 *     onSelect={({ line, city, state, zip }) => setForm(...)}
 *     testId="my-address"
 *   />
 */
import { useState, useEffect, useRef } from "react";
import { Search, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddressAutocomplete({
  label, value, onChange, onSelect, testId, placeholder = "Start typing address…",
  darkOnLight = false,  // opt-in light styling for admin pages
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const runSearch = async (q) => {
    // 2026-02-08 UX bump — trigger at 2 chars (was 3) so short prefixes
    // like "27" or "1736" surface suggestions immediately.
    if (!q || q.trim().length < 2) {
      setSuggestions([]); setOpen(false); return;
    }
    setLoading(true); setOpen(true);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    try {
      const r = await fetch(
        // 2026-02-08 — bumped default limit 6 → 10 for richer dropdowns.
        `${API}/api/address/search?q=${encodeURIComponent(q)}&limit=10`,
        { signal },
      );
      const json = await r.json();
      if (signal.aborted) return;
      const items = (json?.items || []).map((s) => ({
        ...s,
        label: `${s.line}${s.city ? ", " + s.city : ""}${s.state ? ", " + s.state : ""}${s.zip ? " " + s.zip : ""}`,
      }));
      setSuggestions(items);
    } catch {
      // Silent — user can still type manually.
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  const handleChange = (e) => {
    onChange(e);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 2026-02-08 — snappier UX (was 280ms, now 150ms).  Users feel
    // Google-Places-fast without hammering the backend.
    debounceRef.current = setTimeout(() => runSearch(e.target.value), 150);
  };

  const pick = (s) => {
    onSelect(s); setSuggestions([]); setOpen(false); setActiveIdx(-1);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const inputCls = darkOnLight
    ? "w-full pl-9 pr-9 py-2 bg-white border border-zinc-300 rounded-lg text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
    : "w-full pl-9 pr-9 py-2 bg-zinc-950 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-fuchsia-500";

  return (
    <div ref={wrapRef}>
      {label && (
        <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{label}</label>
      )}
      <div className={`relative ${label ? "mt-1" : ""}`}>
        <span className="absolute inset-y-0 left-3 flex items-center text-zinc-500">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </span>
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => value && suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          autoComplete="off"
          className={inputCls}
          data-testid={testId}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange({ target: { value: "" } }); setSuggestions([]); setOpen(false); }}
            className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300"
            tabIndex={-1}
            aria-label="Clear address"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {open && suggestions.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl overflow-hidden"
            data-testid={`${testId}-suggestions`}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-800 last:border-b-0 transition ${
                  i === activeIdx ? "bg-fuchsia-500/20 text-white" : "text-zinc-200 hover:bg-zinc-900"
                }`}
                data-testid={`${testId}-suggestion-${i}`}
              >
                <div className="font-medium flex items-center gap-2">
                  <span>{s.line}</span>
                  {s.usps_verified && (
                    <span className="text-[9px] uppercase tracking-wider bg-emerald-500/25 text-emerald-200 px-1.5 py-0.5 rounded-full font-bold">
                      USPS verified
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-400">
                  {[s.city, s.state, s.zip].filter(Boolean).join(", ")}
                </div>
              </button>
            ))}
          </div>
        )}
        {open && !loading && suggestions.length === 0 && (value || "").length >= 2 && (
          <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl px-3 py-2 text-xs text-zinc-500 italic">
            No matches — you can still type your address manually.
          </div>
        )}
      </div>
    </div>
  );
}
